import { ActivityMode, ContentType, ContentMode } from '@/models/Session';
import { Sibling, type Course } from '@/models/Course';
import { type Lesson } from '@/models/Lesson';

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import slugify from 'slugify';

import { getContentIdFromSlug } from '@/utils/getContentIdFromSlug';

import { getCourseMetadata } from '@/utils/course';
import { getLessonContent } from '@/utils/lesson';
import { handleUnauthorizedRequest } from '@/utils/fetch';

import { track } from '@/analytics/mixpanel';

import { useSessionStore } from './useSessionStore';
import { useNavigationStore } from './useNavigationStore';

export const useContentStore = defineStore('content', () => {
  const route = useRoute();
  const { locale } = useI18n();

  const isLoading = ref<boolean>(false);
  const contentData = ref<Course | Lesson>();

  const sessionStore = useSessionStore();

  const { redirectToNotFoundPage, redirectToInternalErrorPage, removeQueryParams } =
    useNavigationStore();

  const isInitialized = computed(
    () => Boolean(sessionStore.contentId) && Boolean(contentData.value),
  );

  const initializeContent = () => {
    const { contentType, slug } = route.params;

    const newContentId = getContentIdFromSlug(slug as string);
    if (isNaN(newContentId)) {
      console.error('Invalid content id.');
      redirectToNotFoundPage();
      return;
    }

    sessionStore.setContentId(newContentId);

    if (sessionStore.isLms && sessionStore.contentId && newContentId !== sessionStore.contentId) {
      console.error('Unauthorized LMS content access detected.');
      handleUnauthorizedRequest();
      return;
    }

    sessionStore.setContentType(contentType as ContentType);

    initializeContentMode();
  };

  const initializeContentMode = () => {
    if (sessionStore.isLms && route.query.mode) {
      const isValidContentMode = (route.query.mode as string) in ContentMode;
      sessionStore.setContentMode(
        isValidContentMode ? (route.query.mode as ContentMode) : ContentMode.all,
      );
      removeQueryParams();
      return;
    }

    sessionStore.setContentMode(ContentMode.all);
  };

  const fetchContentData = async () => {
    if (isLoading.value || isInitialized.value) return;

    try {
      isLoading.value = true;

      const contentResponse = await getContentData(sessionStore.contentId as number);
      if (!contentResponse) {
        console.error('Cannot get the content.');
        redirectToNotFoundPage();
        return;
      }

      const needsCourseSiblingCheck =
        sessionStore.contentType === ContentType.course &&
        contentResponse.language !== locale.value;
      if (!needsCourseSiblingCheck) {
        setContentData(contentResponse);
        return;
      }

      checkCourseSiblings(contentResponse as Course);
    } catch (error) {
      redirectToInternalErrorPage();
    } finally {
      isLoading.value = false;
    }
  };

  const getContentData = (contentId: number) => {
    return sessionStore.contentType === ContentType.course
      ? getCourseMetadata(contentId)
      : getLessonContent(contentId);
  };

  const setContentData = (newContentData: Course | Lesson) => {
    contentData.value = newContentData;

    trackContentPageVisit();
  };

  const checkCourseSiblings = (courseContent: Course) => {
    const siblingCourse = courseContent.siblings.find(
      (sibling) => sibling.language.toLowerCase() === locale.value,
    );
    if (siblingCourse) {
      redirectToSiblingCoursePage(siblingCourse);
      return;
    }

    // Handle base libraries.
    // https://goodhabitz.atlassian.net/browse/INT-541
    const isSameLanguage = courseContent.language.split('-')[0] === locale.value.split('-')[0];
    const isDifferentLibrary = courseContent.language.split('-')[1] !== locale.value.split('-')[1];
    if (isSameLanguage && isDifferentLibrary) {
      setContentData(courseContent);
      return;
    }

    if (isLanguageChanged()) {
      window.location.href = `/${locale.value}/personal-courses.html`;
      return;
    }

    setContentData(courseContent);
  };

  const redirectToSiblingCoursePage = (siblingCourse: Sibling) => {
    const siblingCourseSlug = `${siblingCourse.id}-${slugify(siblingCourse.title, {
      lower: true,
    })}`;

    window.location.href = `/${locale.value}/courses/${siblingCourseSlug}`;
  };

  const initializeSiblingContent = async () => {
    sessionStore.contentId = getContentIdFromSlug(route.params.slug as string);

    setContentData(await getContentData(sessionStore.contentId as number));
  };

  const trackContentPageVisit = () => {
    if (!contentData.value || sessionStore.activityMode !== ActivityMode.normal) return;

    if (sessionStore.contentType === ContentType.course) {
      track('Pageview');
    } else {
      track('Lesson Visit', { lesson_name: (contentData.value as Lesson).title });
    }
  };

  const isLanguageChanged = () => {
    return document.referrer.includes(`/courses/`) && !document.referrer.includes('?redirect=');
  };

  return {
    contentData,
    isInitialized,
    initializeContent,
    fetchContentData,
    initializeSiblingContent,
  };
});
